import PropTypes from 'prop-types';
import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import { useSelector } from 'react-redux';
import UserAssignmentSelector from './UserAssignmentSelector';

import { isDynamicType } from '../workflowHelpers';
import { resolveFormDivisionIds } from '../../formHelpers';

import config from '../../../config';
import { mergeSets } from '../../../helpers/helpers';

// eslint-disable-next-line no-useless-escape
const emailRegex = /^[^@\s]+@[^@\s\.]+(?:\.[^@\.\s]+)+$/;

const tokenSeparators = [',', '\n'];

const FORM_AUTHOR_KEY = 'formAuthor';
const AUTHOR_HELP = 'The form will be sent to the form\'s author';

const COMPANY_OWNER_KEY = 'companyOwner';
const OWNER_HELP = 'The form will be sent to the company\'s owner';

export default function EmailRecipientSelector({
  sections,
  initialSelected,
  divisionId,
  divisionIds = [],
  onChange,
  additionalOptions,
  text = 'Select Recipient(s):',
  mode = 'tags',
  showFormAuthor,
  authorHelpText = AUTHOR_HELP,
  showCompanyOwner,
  ownerHelpText = OWNER_HELP,
  hideUsers,
  isDisplay,
  placeholder,
  disabled,
}) {
  const [selected, setSelected] = useState(initialSelected || []);
  const [filtered, setFiltered] = useState(false);

  const divisions = useSelector((state) => state.settings.divisions);
  const users = useSelector((state) => state.users.users);

  const userIds = useMemo(() => {
    const idSet = new Set();
    users.forEach((user) => {
      if (user.active) {
        idSet.add(user.id);
      }
    });
    return idSet;
  }, [users]);

  const additionalIds = useMemo(() => new Set(
    additionalOptions.map((addOpt) => addOpt.id),
  ), [additionalOptions]);

  const validFields = useMemo(() => {
    const userFieldIds = new Set();
    sections.forEach(({ fields = [] }) => {
      fields.forEach((field) => {
        const {
          id: fieldId,
          selectedType,
          configProps: {
            dataType,
          } = {},
        } = field;
        if (
          (selectedType === 'dropdown' && dataType === 'Users')
          || isDynamicType(field, 'dropdown', 'Users')
        ) {
          userFieldIds.add(fieldId);
        }
      });
    });
    return userFieldIds;
  }, [sections]);

  const relevantDivisionIds = useMemo(
    () => resolveFormDivisionIds({ divisionIds, divisionId }),
    [divisionIds, divisionId],
  );

  const validate = (selectedValues) => selectedValues.filter((emailOrId) => (
    emailOrId === FORM_AUTHOR_KEY
    || emailOrId === COMPANY_OWNER_KEY
    || userIds.has(emailOrId)
    || additionalIds.has(emailOrId)
    || emailRegex.test(emailOrId)
    || validFields.has(emailOrId)
  ));

  const onRecipientChange = useCallback((newSelected) => {
    const filteredSelected = validate(newSelected);

    setSelected(filteredSelected);
    onChange(filteredSelected);
  }, [onChange]);

  useEffect(() => {
    if (!initialSelected?.length || !relevantDivisionIds.length || filtered) return;

    let ourSelected = initialSelected;
    if (config.showDivisions) {
      const divisionUserIds = mergeSets(
        relevantDivisionIds.map((divId) => divisions[divId]?.users ?? new Set()),
      );
      ourSelected = initialSelected
        .filter((userId) => userId.length !== 36 || divisionUserIds.has(userId));
    }
    onChange(ourSelected);
    setFiltered(true);
  }, [initialSelected, relevantDivisionIds, divisions, filtered, onChange]);

  return (
    <UserAssignmentSelector
      divisions={relevantDivisionIds}
      onChange={onRecipientChange}
      selected={selected}
      mode={mode}
      showPositions={false}
      type="email"
      text={text}
      tokenSeparators={tokenSeparators}
      additionalOptions={additionalOptions}
      sections={sections}
      showFields
      showFormAuthor={showFormAuthor}
      formAuthorHelperText={authorHelpText}
      showCompanyOwner={showCompanyOwner}
      companyOwnerHelpText={ownerHelpText}
      hideUsers={hideUsers}
      isDisplay={isDisplay}
      placeholder={placeholder}
      disabled={disabled}
    />
  );
}

EmailRecipientSelector.propTypes = {
  additionalOptions: PropTypes.array,
  authorHelpText: PropTypes.string,
  divisionId: PropTypes.string,
  divisionIds: PropTypes.arrayOf(PropTypes.string),
  initialSelected: PropTypes.array,
  mode: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  sections: PropTypes.array,
  showFormAuthor: PropTypes.bool,
  text: PropTypes.string,
  showCompanyOwner: PropTypes.bool,
  ownerHelpText: PropTypes.string,
  hideUsers: PropTypes.bool,
  isDisplay: PropTypes.bool,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
};

EmailRecipientSelector.defaultProps = {
  additionalOptions: [],
  authorHelpText: AUTHOR_HELP,
  divisionId: null,
  divisionIds: [],
  initialSelected: [],
  mode: 'tags',
  sections: [],
  showFormAuthor: false,
  text: 'Select Recipient(s):',
  showCompanyOwner: false,
  ownerHelpText: OWNER_HELP,
  hideUsers: false,
  isDisplay: false,
  placeholder: undefined,
  disabled: false,
};
